// ============================================================================
// OddsMarket — one market title + N OddsButtons in a layout-aware row
// ============================================================================
// Three layouts cover every market shape in the sportsbook:
//   row            — 2-3 equal-width buttons (1X2, qualification, O/U pair)
//   stack          — N full-width stacked buttons (e.g. double chance)
//   line-selector  — slider readout + Over/Under row
//
// Selection state is local: only ONE outcome can be active per market. A
// click on a different outcome replaces the current selection. A click on
// the already-selected outcome deselects it. Production wires `onSelect`
// to the global betslip; the local highlight state is independent.
// ============================================================================

import { useState, type ReactElement } from 'react';
import OddsButton from '../atoms/OddsButton.js';
import MarketBadge from '../atoms/MarketBadge.js';
import type { DisplayMarket, OddsOutcome } from '../../types/odds.js';
import styles from './OddsMarket.module.css';

export interface OddsMarketProps {
  market: DisplayMarket;
  /** Optional click handler; receives the outcome id. */
  onSelect?: (outcomeId: number) => void;
  /** Locale-aware decimal separator. Defaults to '.'. */
  decimalSeparator?: '.' | ',';
  /**
   * Suppress the inner `<header>` (title + line + badge). Use when the
   * caller already shows a heading above this card and the duplicate
   * title reads as noise (e.g. MultiMarketStack's collapsible market
   * cards in #456). Default false — standalone uses keep the header.
   *
   * Note: when `hideTitle` is true, any `line` info (e.g. AH -1.25) is
   * lost from view here — the caller is expected to surface it on
   * their own heading via `market.line`.
   */
  hideTitle?: boolean;
  /**
   * Visual treatment of the outcomes (additive, ADR-059).
   *
   *   'pill' (default) — OddsButton pills in the market's `layout`
   *                      (row / stack / line-selector).
   *   'list'           — quiet full-width rows: label left, price
   *                      right, hairline dividers. For ranked
   *                      N-outcome player markets (anytime goalscorer
   *                      in PlayerPropsCard). Ignores `market.layout`;
   *                      selection + suspension keep the pill contract.
   */
  appearance?: 'pill' | 'list';
}

function formatLine(line: number, decimalSeparator: '.' | ','): string {
  const lineStr = line.toFixed(1);
  return decimalSeparator === ',' ? lineStr.replace('.', ',') : lineStr;
}

// LOCKED HOUSE RULE (2026-06-11, mirrors OddsButton): decimal odds PRICES
// always render with '.' on every market and locale — European odds
// notation. Only market LINES follow the locale separator.
function formatPrice(odds: number | null | undefined): string {
  if (odds == null) return '—';
  return odds.toFixed(2);
}

/**
 * Asian Handicap outcomes ship as just the team name — splice the line
 * into the label so the button reads "Team -1.5" instead of just "Team".
 * Engine is expected to ship a compact `label` already; we only add the
 * line suffix.
 */
function formatAhLabel(outcome: OddsOutcome, decimalSeparator: '.' | ','): string {
  if (typeof outcome.line !== 'number') return outcome.label;
  const sign = outcome.line > 0 ? '+' : '';
  const lineStr = `${sign}${formatLine(outcome.line, decimalSeparator)}`;
  return `${outcome.label} ${lineStr}`;
}

export function OddsMarket({
  market,
  onSelect,
  decimalSeparator = '.',
  hideTitle = false,
  appearance = 'pill',
}: OddsMarketProps): ReactElement {
  const { title, layout, badge, line, outcomes } = market;
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const handleClick = (outcomeId: number): void => {
    setSelectedId((current) => (current === outcomeId ? null : outcomeId));
    onSelect?.(outcomeId);
  };

  const isAhMarket = outcomes.some(
    (o) => typeof o.line === 'number' && o.participantName !== undefined,
  );

  // 'list' appearance — quiet ranked rows instead of pills. The market
  // particle still owns the odds display (ADR-059); each row keeps the
  // same select/suspend contract as the pill path.
  const outcomesBlock =
    appearance === 'list' ? (
      <div className={styles.list}>
        {outcomes.map((oc) => {
          const suspended = oc.status === 'SUSPENDED' || oc.odds == null;
          const selected = selectedId === oc.id;
          const stateClass = suspended
            ? styles.listRowSuspended
            : selected
              ? styles.listRowSelected
              : '';
          const priceLabel = formatPrice(oc.odds);
          return (
            <button
              key={oc.id}
              type="button"
              className={`${styles.listRow} ${stateClass}`}
              onClick={suspended ? undefined : (): void => handleClick(oc.id)}
              disabled={suspended}
              aria-pressed={selected ? true : undefined}
              aria-label={`${oc.label} ${priceLabel}`}
            >
              <span className={styles.listLabel}>{oc.label}</span>
              <span className={styles.listPrice}>{priceLabel}</span>
            </button>
          );
        })}
      </div>
    ) : (
      <div className={layout === 'stack' ? styles.stack : styles.row}>
        {outcomes.map((oc) => (
          <OddsButton
            key={oc.id}
            label={isAhMarket ? formatAhLabel(oc, decimalSeparator) : oc.label}
            odds={oc.odds}
            // A 2-3-across market stacks the price over the label; a
            // full-width stack reads label-left / price-right so the
            // prices share a decimal axis (Atlas Universe recipe).
            layout={layout === 'stack' ? 'row' : 'stacked'}
            width="fill"
            suspended={oc.status === 'SUSPENDED'}
            selected={selectedId === oc.id}
            onClick={(): void => handleClick(oc.id)}
          />
        ))}
      </div>
    );

  return (
    <div className={styles.market}>
      {!hideTitle && (
        <div className={styles.header}>
          <span className={styles.title}>
            {title}
            {line !== undefined && (
              <span className={styles.lineSelector}>
                {' · '}
                {formatLine(line, decimalSeparator)}
              </span>
            )}
          </span>
          {badge && <MarketBadge label={badge.label} tone={badge.tone} />}
        </div>
      )}

      {appearance !== 'list' && layout === 'line-selector' && (
        <div className={styles.lineRow} aria-hidden="true">
          <div className={styles.lineTrack}>
            <span className={styles.lineDot} />
          </div>
        </div>
      )}

      {outcomesBlock}
    </div>
  );
}

export default OddsMarket;
